import { Navigate, RouteObject, useLocation } from "react-router";

import { FullScreenLoader } from "@punto/ui/components/full-screen-loader";

/**
 * Where Auth0 sends a login back to, in Janus and Portus alike. Every origin
 * that serves either app needs `<origin>/callback` in the Auth0 application's
 * Allowed Callback URLs.
 */
export const AUTH_CALLBACK_PATH = "/callback";

/** The same test `Auth0Provider` uses to decide it has a login to finish. */
const hasAuthParams = (search: string) => {
  const params = new URLSearchParams(search);

  return params.has("state") && (params.has("code") || params.has("error"));
};

/**
 * Holds the login's landing page while `Auth0Provider` finishes it; its
 * `onRedirectCallback` then replaces this entry with the page the user asked
 * for. Nothing here may navigate meanwhile: React Router cancels a pending
 * navigation when another one starts, and that pending one is the way back to
 * the deep link while its lazy route module loads (P-3142). An error comes
 * back here too and is `AuthErrorGate`'s to handle.
 */
const AuthCallbackScreen = () => {
  const { search } = useLocation();

  if (!hasAuthParams(search)) return <Navigate to="/" replace />;

  return <FullScreenLoader data-testid="auth-callback" />;
};

/** Public on purpose: the user has no session yet when they land here. */
export const authCallbackRoute: RouteObject = {
  path: AUTH_CALLBACK_PATH,
  element: <AuthCallbackScreen />,
};
